import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Signed in successfully");
      const r = res.user.role;
      const from = location.state?.from || "";
      if (from.startsWith("/mobilecrm") && ["admin", "manager", "sales", "sales_manager"].includes(r)) { navigate(from); return; }
      navigate(r === "customer" ? "/welcome" : r === "instrument_customer" ? "/instrumentation/portal" : "/crm");
    } else {
      setError(res.error);
      toast.error(res.error || "Invalid email or password");
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:block bg-[#0F172A] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&q=80&w=1600"
          alt="Aviation"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-[#0F172A]/70 noise-overlay" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <img src="/paslogo.png" alt="Peregrine Companies" className="h-11 w-auto bg-white rounded-md px-3 py-2" />
            <span className="text-white font-display font-bold tracking-tight">PEREGRINE COMPANIES PORTAL</span>
          </div>
          <div>
            <h1 className="text-white font-display font-extrabold text-5xl leading-[0.95] tracking-tight">
              Customer<br />Service<br /><span className="text-[#D4AF37]">First.</span>
            </h1>
            <p className="mt-6 text-zinc-300 max-w-sm text-base leading-relaxed">
              Welcome to the Peregrine Portal
            </p>
          </div>
          <div className="text-xs text-zinc-400 font-mono">
            6318 Union Avenue, Shreveport, LA 71106 · 318.222.2224
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/paslogo.png" alt="Peregrine Companies" className="h-9 w-auto" />
            <span className="font-display font-bold tracking-tight text-[#0F172A]">PEREGRINE COMPANIES PORTAL</span>
          </div>

          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-3">Portal Access</p>
          <h2 className="font-display font-extrabold text-4xl tracking-tight text-[#0F172A]">Sign in</h2>
          <p className="mt-2 text-zinc-500">Use the credentials provided to you by Peregrine.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">Email address</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full px-4 py-3 border border-zinc-300 bg-white text-[#0A0A0A] font-mono text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-[#0F172A]">Password</label>
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs text-zinc-500 hover:text-[#0F172A] transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="w-full px-4 py-3 border border-zinc-300 bg-white text-[#0A0A0A] font-mono text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow"
                placeholder="••••••••••"
              />
            </div>

            {error && (
              <p className="text-sm text-[#EF4444]" data-testid="login-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="group w-full flex items-center justify-center gap-2 bg-[#0F172A] text-white py-3.5 font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Sign in
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-zinc-200">
            <p className="text-sm text-zinc-500">New customer?</p>
            <Link
              to="/register"
              data-testid="register-link"
              className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-[#0F172A] hover:text-[#D4AF37] transition-colors"
            >
              New Customers — Create a Login
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
